﻿import {CANVAS_ID} from "@dp-instant/core";
import {PlayerData} from "../Data/PlayerData";
import {Vibrator} from "./Vibrator";
import {NotificationsManager} from "./NotificationsManager";
import {AnalyticsManager} from "./AnalyticsManager";
import {Localization} from "./Localization";
import {BoostersManager} from "./BoostersManager";
import {TimeManager} from "./TimeManager";
import {TimersManager} from "./TimersManager";
import {AudioManager} from "./AudioManager";
import {AuthManager} from "./AuthManager";
import {AppStore} from "./AppStore";
import {GameData} from "../Data/GameData";
import {LocalSettings} from "./LocalSettings";
import {UICore} from "./UICore";
import {UIState} from "../enums";
import {PlayerPrefs} from "../Utils/Unity/PlayerPrefs";
import {MonoBehaviour} from "../Utils/Unity/MonoBehaviour";
import {VFX} from "../../vfx/VFX";
import {gsap} from "gsap";
import {Time} from "../Utils/Unity/Time";
import {di} from "@dp-instant/core";

class GameCore extends MonoBehaviour {
    Awake() {
        super.Awake();
    }

    async Init(app) {
        this.Initialized = this.Initialized || false;

        this.app = app; // PIXI.Application

        this.Localizator = new Localization();
        this.Localizator.Init(this.app.loader.resources["LOCALIZATION"].data);

        this.Auth = new AuthManager();
        this.Store = new AppStore();
        this.Audio = new AudioManager();
        this.Time = new TimeManager();
        this.Timers = new TimersManager();
        this.Boosters = new BoostersManager();
        this.Ads = di.get("AdsProviderCommon");
        this.Analytics = new AnalyticsManager();
        this.Notifications = new NotificationsManager();
        this.Vibro = new Vibrator();

        // Data
        this.Player = new PlayerData();
        this.GameData = new GameData();
        this.LocalSettings = new LocalSettings();
        this.UI = new UICore();
        this.VFX = new VFX();

        this.savePlayerDataTimer = 0;
        this.savePlayerDataDelta = 60;

        this._waitingCoroutines = [];

        this.onPlayerDataChanged = this.onPlayerDataChanged.bind(this);
        this.tick = this.tick.bind(this);

        await this.GameData.Init();
        await this.Auth.LoadLocal();
        await this.Time.Init();
        await this.UI.Init();
        await this.Store.Init();
        await this.Audio.Init();
        await this.Timers.Init();
        await this.Boosters.Init();
        await this.Ads.Init();

        if (!this.Initialized) {
            this.Player.on("Changed", this.onPlayerDataChanged);
            //
            //FBInstant.onPause(() => this.SetPause(true));
            //
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState !== "visible") this.SetPause(true);
            });
        }

        this.Initialized = true;

        // Run initial update
        this.Player.emit("Changed");
        this.Auth.once("AuthComplete", this.onAuthComplete.bind(this));
    }

    SetPause(value, noPopup = false) {
        if (!this.Initialized) return;
        this.Audio.SetMuted(!!value);
        this.app.paused = !!value;
        if (value) {
            if (!noPopup) this.UI.pausePopup.Show();
            gsap.globalTimeline.pause();
        } else {
            this.UI.pausePopup.Hide();
            gsap.globalTimeline.resume();
        }
    }

    Authenticate() {
        this.Auth.Authenticate();
    }

    tick(deltaTime) {
        // Tick coroutines (see Unity manual for details)
        for (let i = this._waitingCoroutines.length - 1; i >= 0; i--) {
            this._waitingCoroutines[i].seconds -= deltaTime;
            if (this._waitingCoroutines[i].seconds <= 0) {
                let {action} = this._waitingCoroutines[i];
                this._waitingCoroutines.splice(i, 1);
                action();
            }
        }

        this.savePlayerDataTimer -= deltaTime;
        if (this.savePlayerDataTimer <= 0) {
            this.Auth.SaveLocal();
            this.savePlayerDataTimer = this.savePlayerDataDelta;
        }

        this.Timers.Update();
        this.UI.Update();
        this.Player.EmitOnChanged();

        this.emit("Updated");
    }

    WaitBefore(seconds, action) {
        this._waitingCoroutines.push({seconds, action});
    }

    DeleteAllPlayerPrefs() {
        PlayerPrefs.DeleteKey("wuyfn283rtycn83etfc3rt4c");
        PlayerPrefs.DeleteKey("ueicyn5784t35yc83457cty");
        PlayerPrefs.DeleteKey("ruy3n874xz3948rymx34r");
        PlayerPrefs.DeleteKey("o54iubym4u59846vm345");
        PlayerPrefs.DeleteKey("43v89netyc8nert3cn834crt");
        PlayerPrefs.DeleteKey("eruycberuwtfxb832tb46tr384");
        PlayerPrefs.DeleteKey("starterpack");
        PlayerPrefs.DeleteKey("gdpr");
        PlayerPrefs.DeleteKey("eruicty8n574t6yvn34yxt23");
        PlayerPrefs.DeleteKey("uewytn86b382txr7b2z23ex23");
        PlayerPrefs.DeleteKey("vippopup");
        PlayerPrefs.DeleteKey("timeOfLastBubbleSpawn");
        PlayerPrefs.DeleteKey("timeOfLastBubbleChargeSpawn");
        PlayerPrefs.DeleteKey("ruetvhnruivghrnite");
        PlayerPrefs.DeleteKey("cr2tbct34bc72tbx32dx56v3r");
        PlayerPrefs.DeleteKey("2ctynyv5vb4n572c4334");
        PlayerPrefs.DeleteKey("tutorPressDropPod");

        PlayerPrefs.DeleteKey("difygc34ne98rtycnerfcefcr1");
        PlayerPrefs.DeleteKey("difygc34ne98rtycnerfcefcr2");
        PlayerPrefs.DeleteKey("difygc34ne98rtycnerfcefcr3");
        PlayerPrefs.DeleteKey("difygc34ne98rtycnerfcefcr4");
        PlayerPrefs.DeleteKey("difygc34ne98rtycnerfcefcr5");
        PlayerPrefs.DeleteKey("difygc34ne98rtycnerfcefcr6");

        for (let p of this.GameData.Planets.planets) {
            PlayerPrefs.DeleteKey("timeOfLastBubbleSpawn" + p.id);
        }
    }

    onPlayerDataChanged() {
        this.Auth.SaveLocal();
    }

    onAuthComplete(success) {
        this.Auth.off("AuthComplete", this.onAuthComplete);
        this.Time.Init();
    }

    onGameSceneLoaded() {
        this.VFX.Init(this.app.vfx);

        this.UI.SetState(UIState.Game);
        this.Initialized = true;
        this.Audio.PlayLobbyTheme();

        this.UI.moneyBox.Check();
        this.UI.vipDailyReward.Check();

        this.WaitBefore(2, () => {
            this.UI.vip.ShowAgressive();
        });
        this.UI.gdpr.Trigger();
        this.Auth.SaveLocal();
    }

    OnApplicationQuit() {
        if (this.Player.Initialized) {
            this.Auth.SaveLocal();
        }
    }

    OnApplicationPause(isPause) {
        if (this.Player.Initialized) {
            this.Auth.SaveLocal();
        }
    }

    async Reset() {
        this.SetPause(true, true);
        await PlayerPrefs.Reset();
        await this.Auth.Reset();
        await this.Store.Reset();
        await this.Audio.Reset();

        if (this.app) {
            this.app.destroy(true);
            this.app = window.app = null;
        }

        this.Initialized = undefined;

        PIXI.utils.clearTextureCache();

        let canvas = document.createElement("canvas");
        // canvas.setAttribute("id", "scene");
        canvas.setAttribute("id", CANVAS_ID);
        document.getElementById("game-root").appendChild(canvas);

        const {Application} = await import("../../Application");
        this.app = window.app = Application.create();
        await this.app.load();

        Time.reset();
        await this.app.run();

        this.UI.settings.hideResetBtn();

        this.SetPause(false);
    }
}

const Core = new GameCore();
export {Core};
